import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { ErrorMessage } from '@wilson/utils';
import { Observable, Subject, debounceTime, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  LogoutAndNavigateToAuth,
  RefreshAccessToken,
} from '../state/auth.actions';

/** The scheme JwtInterceptor (@auth0/angular-jwt) prefixes our access token with by default. */
const BEARER_PREFIX = 'Bearer ';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);

  private requestToRefreshSubject = new Subject<boolean>();
  private accessTokenSubject = new Subject<string | null>();

  constructor() {
    this.requestToRefreshSubject
      .pipe(
        debounceTime(100),
        switchMap(() =>
          this.dispatchRefresh().pipe(map(() => this.currentAccessToken())),
        ),
      )
      .subscribe((accessToken) => {
        this.accessTokenSubject.next(accessToken);
      });
  }

  private currentAccessToken(): string | null {
    return this.store.snapshot()?.auth?.accessToken ?? null;
  }

  /** The access token JwtInterceptor attached to this request, or null when it attached none. */
  private sentAccessToken(req: HttpRequest<unknown>): string | null {
    const authorization = req.headers.get('Authorization');
    if (!authorization?.startsWith(BEARER_PREFIX)) return null;
    return authorization.slice(BEARER_PREFIX.length) || null;
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const reqNew = req.clone({
      setHeaders: { 'Full-Referer': document.location.href },
    });

    return next.handle(reqNew).pipe(
      catchError((error) => {
        if (error.status !== 401) return throwError(() => error);
        if (req.headers.get('X-Ignore-Auth-Interceptor')) {
          return throwError(() => error);
        }
        // Suspended tenant: refresh is pointless (backend blocks it too) — log out now.
        if (error.error?.message === ErrorMessage.TenantSuspended) {
          return this.handleTenantSuspended(error);
        }

        // Only a request that carried our token can be recovered with a new one. Anything else
        // (a third-party call, or one sent while logged out) passes its 401 through untouched,
        // so our token is never attached to a request that did not have it.
        const sentToken = this.sentAccessToken(req);
        if (!sentToken) return throwError(() => error);

        return this.handle401Error(req, next, sentToken, error);
      }),
    );
  }

  private addTokenToRequest(
    req: HttpRequest<unknown>,
    token: string,
  ): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  private handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    sentToken: string,
    originalError: unknown,
  ): Observable<HttpEvent<unknown>> {
    // Already refreshed in flight: replay now, as a same-second refresh returns an identical token.
    const currentToken = this.currentAccessToken();
    if (currentToken && currentToken !== sentToken) {
      return next.handle(this.addTokenToRequest(req, currentToken));
    }

    this.requestToRefreshSubject.next(true);
    return this.accessTokenSubject.asObservable().pipe(
      take(1),
      switchMap((token) =>
        // A temporarily failed refresh keeps sentToken; replaying with it would 401 again and loop.
        token && token !== sentToken
          ? next.handle(this.addTokenToRequest(req, token))
          : throwError(() => originalError),
      ),
    );
  }

  // RefreshAccessToken handles its own failures and never errors; see AuthState.refreshAccessToken.
  private dispatchRefresh(): Observable<void> {
    return this.store.dispatch(new RefreshAccessToken());
  }

  private handleTenantSuspended(error: unknown): Observable<never> {
    return this.store
      .dispatch(new LogoutAndNavigateToAuth({ suspended: '1' }))
      .pipe(switchMap(() => throwError(() => error)));
  }
}
